import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Check, ChevronRight, ChevronLeft, Upload, FileText, X,
  GraduationCap, Briefcase, Award, Rocket
} from 'lucide-react'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { programmeLabel } from '../../utils/programme'
import toast from 'react-hot-toast'

const STEP_LABELS = ['PROFILE', 'PROGRAMME', 'RESEARCH', 'REFERENCES', 'CONFIRM']

const UNIVERSITIES = [
  'Kennedy University',
  'Dunster Business School',
  'London School of Business and Science (LSBS)',
  'Woxsen University',
]

const PROGRAMMES = [
  { value: 'PhD',               icon: GraduationCap, label: 'PhD',      desc: 'Doctor of Philosophy' },
  { value: 'DBA',               icon: Briefcase,     label: 'DBA',      desc: 'Doctor of Business Administration' },
  { value: 'Honorary Doctorate',icon: Award,         label: 'Honorary', desc: 'Honorary Doctorate' },
]

const DOMAINS = [
  'Management', 'Business', 'Social Sciences', 'Technology',
  'Environmental Policy', 'Healthcare', 'Education', 'Finance', 'Law', 'Other',
]

const CURRENT_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: 9 }, (_, i) => CURRENT_YEAR + i)

function inputCls(err) {
  return [
    'w-full bg-white border rounded-lg px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400',
    'outline-none transition-all duration-200',
    'focus:ring-2 focus:ring-brand-500/20',
    err ? 'border-red-400 focus:border-red-400' : 'border-slate-200 focus:border-blue-400',
  ].join(' ')
}

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const { user, profile, updateProfile } = useAuthStore()

  const [step, setStep]         = useState(0)
  const [submitting, setSubmit] = useState(false)
  const [errors, setErrors]     = useState({})
  const [kwInput, setKwInput]   = useState('')
  const [files, setFiles]       = useState([])
  const [dragging, setDragging] = useState(false)
  const fileRef       = useRef(null)
  const submittingRef = useRef(false)

  const [form, setForm] = useState({
    fullName:         profile?.full_name || user?.email?.split('@')[0] || '',
    university:       profile?.university || '',
    customUniversity: '',
    // Left blank on purpose: validate() already requires a choice, and
    // pre-selecting PhD is how DBA users ended up with a PhD profile.
    programme:        profile?.programme || '',
    supervisorName:   profile?.supervisor_name || '',
    enrollmentNumber: profile?.enrollment_number || '',
    submissionYear:   profile?.submission_year || '',
    researchTopic:    profile?.research_topic || '',
    domain:           profile?.domain || '',
    keywords:         profile?.keywords || [],
    rawReferences:    profile?.raw_references || '',
  })

  const set$ = (key, val) => setForm(f => ({ ...f, [key]: val }))
  const on$  = (key) => (e) => set$(key, e.target.value)

  const validate = (s) => {
    const e = {}
    if (s === 0 && form.fullName.trim().length < 2) e.fullName = 'Name must be at least 2 characters'
    if (s === 1) {
      const uni = form.university === 'Other' ? form.customUniversity : form.university
      if (!uni.trim()) e.university = 'University is required'
      if (!form.programme) e.programme = 'Select a programme'
    }
    if (s === 2 && !form.researchTopic.trim()) e.researchTopic = 'Please describe your research area'
    return e
  }

  const go = (next) => {
    const errs = validate(step)
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    setStep(next)
  }

  const addKeyword = (raw) => {
    const kw = raw.trim().replace(/,$/, '')
    if (!kw || form.keywords.includes(kw) || form.keywords.length >= 10) return
    set$('keywords', [...form.keywords, kw])
    setKwInput('')
  }

  const addFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(f => f.size <= 10 * 1024 * 1024).slice(0, 5 - files.length)
    setFiles(prev => [...prev, ...valid].slice(0, 5))
  }

  const handleSubmit = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmit(true)
    try {
      const uni = form.university === 'Other' ? form.customUniversity : form.university
      const payload = {
        fullName: form.fullName.trim(), university: uni, programme: form.programme,
        supervisorName: form.supervisorName, enrollmentNumber: form.enrollmentNumber,
        submissionYear: form.submissionYear, researchTopic: form.researchTopic,
        domain: form.domain, keywords: form.keywords, rawReferences: form.rawReferences,
        onboardingComplete: true,
      }

      let savedProfile = null
      try {
        const { data } = await api.put('/profile', payload)
        if (!data?.profile) throw new Error('invalid')
        savedProfile = data.profile
      } catch {
        // Backend unavailable — write directly to Supabase
        if (!user?.id) throw new Error('Not authenticated')
        const dbPayload = {
          full_name:          payload.fullName,
          university:         payload.university,
          programme:          payload.programme,
          supervisor_name:    payload.supervisorName,
          enrollment_number:  payload.enrollmentNumber,
          submission_year:    payload.submissionYear ? parseInt(payload.submissionYear) : null,
          research_topic:     payload.researchTopic,
          domain:             payload.domain,
          keywords:           payload.keywords,
          raw_references:     payload.rawReferences,
          onboarding_complete: true,
          updated_at:         new Date().toISOString(),
        }
        const { data: profileData, error } = await supabase
          .from('profiles')
          .update(dbPayload)
          .eq('id', user.id)
          .select()
          .single()
        if (error) throw error
        savedProfile = profileData
      }

      updateProfile(savedProfile)
      toast.success('Welcome to AcdyON Scholar!')
      navigate('/app/dashboard', { replace: true })
    } catch (err) {
      toast.error(err?.message || 'Failed to save profile')
      submittingRef.current = false
      setSubmit(false)
    }
  }

  const steps = [
    <StepPersonal  form={form} on$={on$}  errors={errors} userEmail={user?.email} />,
    <StepProgramme form={form} set$={set$} on$={on$} errors={errors} />,
    <StepResearch  form={form} on$={on$}  errors={errors}
                   kwInput={kwInput} setKwInput={setKwInput}
                   addKeyword={addKeyword} set$={set$} />,
    <StepReferences form={form} on$={on$}
                    files={files} setFiles={setFiles}
                    dragging={dragging} setDragging={setDragging}
                    addFiles={addFiles} fileRef={fileRef} />,
    <StepConfirm   form={form} files={files} submitting={submitting} onSubmit={handleSubmit} />,
  ]

  const isLast  = step === 4
  const isFirst = step === 0

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: '#0f172a',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }}
    >
      {/* Progress header */}
      <div className="w-full max-w-2xl mb-8">
        <div className="flex items-end justify-between mb-3 px-1">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex flex-col items-center gap-1.5" style={{ width: '20%' }}>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
                style={{
                  background:    i <= step ? '#2563eb' : 'transparent',
                  border:        i > step ? '1.5px solid #334155' : 'none',
                  outline:       i === step ? '3px solid rgba(37,99,235,0.25)' : 'none',
                  outlineOffset: '2px',
                }}
              >
                {i < step
                  ? <Check className="w-3.5 h-3.5" style={{ color: '#fff' }} strokeWidth={3} />
                  : <span className="font-mono text-xs font-bold"
                      style={{ color: i === step ? '#fff' : '#475569' }}>{i + 1}</span>
                }
              </div>
              <span className="font-mono text-center leading-none"
                style={{ fontSize: 10, letterSpacing: '0.1em', color: i === step ? '#60a5fa' : i < step ? '#3b82f6' : '#475569' }}>
                {label}
              </span>
            </div>
          ))}
        </div>
        <div className="h-px w-full" style={{ background: '#1e293b' }}>
          <div className="h-full transition-all duration-500" style={{ width: `${(step / 4) * 100}%`, background: '#2563eb' }} />
        </div>
      </div>

      {/* Step card */}
      <div className="w-full max-w-2xl rounded-xl overflow-hidden"
        style={{ background: '#1e293b', border: '1px solid #334155', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
        <div style={{ minHeight: '420px' }}>
          <div className="p-8 md:p-10">{steps[step]}</div>
        </div>

        {/* Navigation */}
        {!isLast && (
          <div className="flex items-center justify-between px-8 md:px-10 py-5"
            style={{ borderTop: '1px solid #334155' }}>
            <button
              onClick={() => go(step - 1)}
              disabled={isFirst}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30"
              style={{ color: '#94a3b8' }}
              onMouseEnter={e => { if (!isFirst) e.currentTarget.style.color = '#e2e8f0' }}
              onMouseLeave={e => { if (!isFirst) e.currentTarget.style.color = '#94a3b8' }}
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            <div className="flex items-center gap-4">
              {step === 3 && (
                <button onClick={() => go(step + 1)}
                  className="text-sm" style={{ color: '#475569' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
                  onMouseLeave={e => e.currentTarget.style.color = '#475569'}>
                  Skip for now
                </button>
              )}
              <button
                onClick={() => go(step + 1)}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{ background: '#2563eb', color: '#fff' }}
                onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
                onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Step 1: Personal ────────────────────────────────────────────────────────

function StepPersonal({ form, on$, errors, userEmail }) {
  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#f1f5f9', fontWeight: 700 }}>
          Let's start with you
        </h2>
        <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
          Your name will appear exactly as entered on your thesis cover page.
        </p>
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Full Name</label>
          <input type="text" value={form.fullName} onChange={on$('fullName')}
            placeholder="Dr. Jane Smith" autoFocus className={inputCls(!!errors.fullName)} />
          {errors.fullName && <p className="mt-1 text-xs text-red-400">{errors.fullName}</p>}
          <p className="mt-1 text-xs" style={{ color: '#475569' }}>As it should appear on your thesis</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Email</label>
          <div className="px-3.5 py-2.5 rounded-lg text-sm" style={{ background: '#0f172a', border: '1px solid #334155', color: '#64748b' }}>
            {userEmail}
          </div>
          <p className="mt-1 text-xs" style={{ color: '#475569' }}>Pre-filled from your account — cannot be changed here</p>
        </div>
      </div>
    </div>
  )
}

// ─── Step 2: Programme ───────────────────────────────────────────────────────

function StepProgramme({ form, set$, on$, errors }) {
  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#f1f5f9', fontWeight: 700 }}>
          Your doctoral programme
        </h2>
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>University</label>
          <input list="universities" value={form.university} onChange={on$('university')}
            placeholder="Type or select your university" className={inputCls(!!errors.university)} />
          <datalist id="universities">
            {UNIVERSITIES.map(u => <option key={u} value={u} />)}
          </datalist>
          {errors.university && <p className="mt-1 text-xs text-red-400">{errors.university}</p>}
          {form.university === 'Other' && (
            <input value={form.customUniversity} onChange={on$('customUniversity')}
              placeholder="Enter your university name" className={inputCls(false) + ' mt-2'} />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#94a3b8' }}>Programme Type</label>
          {errors.programme && <p className="mb-2 text-xs text-red-400">{errors.programme}</p>}
          <div className="grid grid-cols-3 gap-3">
            {PROGRAMMES.map(({ value, icon: Icon, label, desc }) => {
              const active = form.programme === value
              return (
                <button key={value} type="button" onClick={() => set$('programme', value)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all text-center"
                  style={{
                    background: active ? 'rgba(37,99,235,0.15)' : '#0f172a',
                    border:     active ? '2px solid #2563eb' : '1.5px solid #334155',
                  }}>
                  <Icon className="w-6 h-6" style={{ color: active ? '#60a5fa' : '#475569' }} />
                  <span className="font-sans font-semibold text-sm" style={{ color: active ? '#93c5fd' : '#cbd5e1' }}>{label}</span>
                  <span className="font-sans text-xs leading-tight" style={{ color: '#64748b' }}>{desc}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Supervisor Name <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
            <input value={form.supervisorName} onChange={on$('supervisorName')}
              placeholder="Prof. Dr. Name" className={inputCls(false)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Enrollment Number <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
            <input value={form.enrollmentNumber} onChange={on$('enrollmentNumber')}
              placeholder="e.g. KU-2024-001" className={inputCls(false)} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Expected Submission Year <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
          <select value={form.submissionYear} onChange={on$('submissionYear')}
            className={inputCls(false)} style={{ appearance: 'none', cursor: 'pointer' }}>
            <option value="">Select year…</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
    </div>
  )
}

// ─── Step 3: Research ────────────────────────────────────────────────────────

function StepResearch({ form, on$, errors, kwInput, setKwInput, addKeyword, set$ }) {
  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#f1f5f9', fontWeight: 700 }}>
          What are you researching?
        </h2>
        <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
          Be as broad or specific as you like — we'll help refine it.
        </p>
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Thesis Topic / Research Area</label>
          <textarea value={form.researchTopic} onChange={on$('researchTopic')} rows={4}
            placeholder="e.g. The impact of digital transformation on SME performance in emerging markets"
            className={inputCls(!!errors.researchTopic) + ' resize-none'} />
          {errors.researchTopic && <p className="mt-1 text-xs text-red-400">{errors.researchTopic}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>Primary Domain</label>
          <select value={form.domain} onChange={on$('domain')}
            className={inputCls(false)} style={{ appearance: 'none', cursor: 'pointer' }}>
            <option value="">Select domain…</option>
            {DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium" style={{ color: '#94a3b8' }}>Keywords <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span></label>
            <span className="font-mono text-xs" style={{ color: '#475569' }}>{form.keywords.length}/10</span>
          </div>
          <input value={kwInput} onChange={e => setKwInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addKeyword(kwInput) } }}
            onBlur={() => addKeyword(kwInput)}
            placeholder="Add keywords and press Enter"
            disabled={form.keywords.length >= 10}
            className={inputCls(false)} />
          {form.keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {form.keywords.map(kw => (
                <span key={kw} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(37,99,235,0.15)', color: '#93c5fd', border: '1px solid rgba(37,99,235,0.3)' }}>
                  {kw}
                  <button type="button" onClick={() => set$('keywords', form.keywords.filter(k => k !== kw))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Step 4: References ──────────────────────────────────────────────────────

function StepReferences({ form, on$, files, setFiles, dragging, setDragging, addFiles, fileRef }) {
  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#f1f5f9', fontWeight: 700 }}>
          Got existing research?
        </h2>
        <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
          Upload papers, bibliography, or prior work — we'll use them to personalise your documents.
        </p>
      </div>

      <div className="rounded-xl p-8 text-center transition-colors cursor-pointer"
        style={{ border: `2px dashed ${dragging ? '#3b82f6' : '#334155'}`, background: dragging ? 'rgba(37,99,235,0.05)' : 'transparent' }}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files) }}
        onClick={() => fileRef.current?.click()}>
        <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: '#475569' }} />
        <p className="text-sm mb-1" style={{ color: '#94a3b8' }}>
          Drop files here or <span style={{ color: '#60a5fa' }}>click to browse</span>
        </p>
        <p className="text-xs" style={{ color: '#475569' }}>PDF, DOCX, TXT · max 5 files · 10MB each</p>
        <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.txt" className="hidden"
          onChange={e => addFiles(e.target.files)} />
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg"
              style={{ background: '#0f172a', border: '1px solid #334155' }}>
              <FileText className="w-4 h-4 shrink-0" style={{ color: '#60a5fa' }} />
              <span className="flex-1 text-sm truncate" style={{ color: '#e2e8f0' }}>{file.name}</span>
              <span className="font-mono text-xs shrink-0" style={{ color: '#475569' }}>
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </span>
              <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                className="transition-colors" style={{ color: '#475569' }}
                onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                onMouseLeave={e => e.currentTarget.style.color = '#475569'}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1.5" style={{ color: '#94a3b8' }}>
          Or paste your bibliography <span style={{ color: '#475569', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea value={form.rawReferences} onChange={on$('rawReferences')} rows={5}
          placeholder="Paste your reference list here…"
          className={inputCls(false) + ' resize-none'} />
      </div>
    </div>
  )
}

// ─── Step 5: Confirm ─────────────────────────────────────────────────────────

function StepConfirm({ form, files, submitting, onSubmit }) {
  const uni = form.university === 'Other' ? form.customUniversity : form.university
  const rows = [
    { label: 'Full Name',   value: form.fullName },
    { label: 'University',  value: uni },
    { label: 'Programme',   value: form.programme },
    { label: 'Supervisor',  value: form.supervisorName || '—' },
    { label: 'Domain',      value: form.domain || '—' },
    { label: 'Submission',  value: form.submissionYear || '—' },
    { label: 'Topic',       value: form.researchTopic, full: true },
    { label: 'Keywords',    value: form.keywords.join(', ') || '—', full: true },
    { label: 'Files',       value: files.length ? `${files.length} file(s) attached` : 'None', full: true },
  ]

  return (
    <div className="space-y-6">
      <div className="mb-6 text-center">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)' }}>
          <Check className="w-7 h-7" style={{ color: '#60a5fa' }} />
        </div>
        <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: '2rem', color: '#60a5fa', fontWeight: 700 }}>
          You're all set.
        </h2>
        <p className="mt-2 text-sm" style={{ color: '#64748b' }}>
          Here's what we have. You can always edit this later.
        </p>
      </div>

      <div className="rounded-xl p-5 space-y-3" style={{ background: '#0f172a', border: '1px solid #334155' }}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {rows.filter(r => !r.full).map(({ label, value }) => (
            <div key={label} className="contents">
              <span className="text-xs" style={{ color: '#475569' }}>{label}</span>
              <span className="text-sm" style={{ color: '#e2e8f0' }}>{value}</span>
            </div>
          ))}
        </div>
        {rows.filter(r => r.full).map(({ label, value }) => (
          <div key={label} className="pt-2" style={{ borderTop: '1px solid #1e293b' }}>
            <span className="text-xs block mb-1" style={{ color: '#475569' }}>{label}</span>
            <span className="text-sm" style={{ color: '#e2e8f0' }}>{value}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onSubmit}
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-bold transition-all disabled:opacity-60"
        style={{ background: '#2563eb', color: '#fff', boxShadow: submitting ? 'none' : '0 0 24px rgba(37,99,235,0.3)' }}
        onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#1d4ed8' }}
        onMouseLeave={e => { if (!submitting) e.currentTarget.style.background = '#2563eb' }}
      >
        {submitting ? 'Launching…' : <><Rocket className="w-4 h-4" /> Launch My {programmeLabel(form.programme)} Journey →</>}
      </button>
    </div>
  )
}
